import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex items-center gap-x-2" };
const _hoisted_2 = { class: "flex flex-col gap-0.5" };
const _hoisted_3 = {
    key: 0,
    class: "text-base text-(--text-text-body)"
};
const _hoisted_4 = ["aria-label"];
const _hoisted_5 = {
    key: 0,
    class: "flex gap-4 items-center"
};
import { Alert, Button, CardSelectable, ConfirmDeleteModal, Modal, SelectInput, TextInput, UrlInput } from "@eudonet/opal";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useSocialNetworkStore } from "@/shared/stores/useSocialNetworkStore";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SocialProfileFormModal',
    props: {
        modelValue: { type: Boolean },
        profile: {},
        platformPickerEnabled: { type: Boolean },
        deleteEnabled: { type: Boolean },
        existingNetworkIds: {},
        error: {},
        loading: { type: Boolean }
    },
    emits: ["update:modelValue", "save", "delete"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const store = useSocialNetworkStore();
        const titleId = `social-profile-form-${Math.random().toString(36).slice(2, 10)}`;
        const isEdit = computed(() => !!props.profile?.id);
        const form = reactive({
            networkId: "",
            value: "",
            label: null,
        });
        const valueTouched = ref(false);
        const showDeleteConfirm = ref(false);
        // Lazy-fetch catalog on first open. The store dedupes concurrent loads and
        // stays cached for the session, so opening the modal a second time is a no-op.
        const loadingCatalog = computed(() => store.loading && store.networks.length === 0);
        watch(() => props.modelValue, (open) => {
            if (open) {
                void store.load();
                form.networkId = props.profile?.networkId ?? "";
                form.value = props.profile?.value ?? "";
                form.label = props.profile?.label ?? null;
                valueTouched.value = false;
                showDeleteConfirm.value = false;
            }
        }, { immediate: true });
        const networkOptions = computed(() => store.networks.map((n) => ({
            value: n.id,
            label: n.name,
        })));
        const availableNetworks = computed(() => store.networks.filter((n) => n.isActive));
        const selectedNetwork = computed(() => store.byId(form.networkId));
        // The contact UrlInput stores a handle (or a full URL for prefix-less networks
        // like Website), never a validated URL, so its built-in URL-format check would
        // reject valid handles. Bypass it; required/length/prefix rules live in
        // `valueError`/`canSave` below. Mirrors SocialProfileList's `acceptAnyValue`.
        const acceptAnyValue = () => undefined;
        // OP-6094: for prefix-based platforms the identifier is a bare handle — the base
        // URL is already supplied by the greyed prefix. Per the AC it must not START WITH
        // `http`, `www` or `/`. This is a bare-prefix rule (case-insensitive): a pasted
        // URL ("https://…", "www.…", "/…") is rejected, and so is a handle that merely
        // begins with those letters ("httptom", "wwwjohn") — the AC forbids the prefix
        // outright. Website (no urlPrefix) is a free full URL and is exempt.
        function hasInvalidHandlePrefix() {
            return !!selectedNetwork.value?.urlPrefix && /^(http|www|\/)/i.test(form.value.trim());
        }
        function isNetworkDisabled(networkId) {
            return (props.existingNetworkIds ?? []).includes(networkId);
        }
        function selectNetwork(networkId) {
            if (isNetworkDisabled(networkId))
                return;
            if (form.networkId === networkId)
                return;
            form.networkId = networkId;
            form.value = "";
            valueTouched.value = false;
        }
        // When the catalog entry has a `urlPrefix` the value is a handle; otherwise
        // the user enters a full URL. The placeholder/help text adapts so the user
        // knows what to type without seeing the actual prefix in the input.
        const valuePlaceholder = computed(() => {
            const net = selectedNetwork.value;
            if (!net)
                return "";
            return net.urlPrefix
                ? t("socialProfile.common.valuePlaceholder.handle")
                : t("socialProfile.common.valuePlaceholder.url");
        });
        const valueHelp = computed(() => {
            const net = selectedNetwork.value;
            if (!net)
                return "";
            return net.urlPrefix
                ? t("socialProfile.common.valueHelp.handle", { prefix: net.urlPrefix })
                : t("socialProfile.common.valueHelp.url");
        });
        const valueError = computed(() => {
            if (!valueTouched.value)
                return undefined;
            if (!form.value.trim())
                return t("socialProfile.common.valueRequired");
            if (form.value.length > 255)
                return t("socialProfile.common.valueTooLong");
            if (hasInvalidHandlePrefix())
                return t("socialProfile.common.valueInvalidPrefix");
            return undefined;
        });
        const canSave = computed(() => {
            if (!form.networkId)
                return false;
            if (!form.value.trim())
                return false;
            if (form.value.length > 255)
                return false;
            if (hasInvalidHandlePrefix())
                return false;
            return true;
        });
        function onNetworkChange(_value) {
            // Reset value/touched when the user picks a different network — handles for
            // LinkedIn make no sense once the user picks a freeform-URL network like
            // "Website". Pre-fill goes through the open-watcher and does not emit, so
            // this handler only fires on real user clicks.
            form.value = "";
            valueTouched.value = false;
        }
        function handleSave() {
            if (!canSave.value) {
                valueTouched.value = true;
                return;
            }
            const net = selectedNetwork.value;
            if (!net)
                return;
            emit("save", {
                id: props.profile?.id ?? "",
                networkId: net.id,
                networkCode: net.code,
                networkName: net.name,
                urlPrefix: net.urlPrefix,
                iconClass: net.iconClass,
                value: form.value.trim(),
                label: form.label && form.label.trim() ? form.label.trim() : null,
            });
            // Organisation flow: modal closes itself. Contact flow (platformPickerEnabled):
            // parent controls close so the error prop can surface BFF failures (AC3).
            if (!props.platformPickerEnabled) {
                emit("update:modelValue", false);
            }
        }
        function handleDeleteConfirm() {
            showDeleteConfirm.value = false;
            if (props.profile) {
                emit("delete", props.profile);
                // Main modal stays open — parent closes it after successful BFF DELETE call.
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    "model-value": __props.modelValue,
                    size: __props.platformPickerEnabled && !isEdit.value ? 'md' : 'sm',
                    "show-close-button": true,
                    "footer-position": __props.deleteEnabled && isEdit.value ? 'split' : 'right',
                    "aria-labelledby": titleId,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => (_ctx.$emit('update:modelValue', $event)))
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _cache[11] || (_cache[11] = _createElementVNode("span", {
                                class: "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-(--border-border-base-soft) bg-white",
                                "aria-hidden": "true"
                            }, [
                                _createElementVNode("i", { class: "ri-share-line text-xl text-(--text-text-black-default)" })
                            ], -1 /* CACHED */)),
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("h2", {
                                    id: titleId,
                                    class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                                }, _toDisplayString(isEdit.value
                                    ? _ctx.$t("socialProfile.common.editTitle")
                                    : _ctx.$t("socialProfile.common.addTitle")), 1 /* TEXT */),
                                (__props.platformPickerEnabled && !isEdit.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t("socialProfile.common.platformPickerSubtitle")), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true)
                            ])
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            size: "md",
                            label: _ctx.$t('socialProfile.common.cancel'),
                            onClick: _cache[6] || (_cache[6] = ($event) => (_ctx.$emit('update:modelValue', false)))
                        }, null, 8 /* PROPS */, ["label"]),
                        (__props.deleteEnabled && isEdit.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _createVNode(_unref(Button), {
                                    variant: "error-secondary",
                                    size: "md",
                                    "icon-left": "ri-delete-bin-line",
                                    label: _ctx.$t('socialProfile.common.delete'),
                                    onClick: _cache[7] || (_cache[7] = ($event) => (showDeleteConfirm.value = true))
                                }, null, 8 /* PROPS */, ["label"]),
                                _createVNode(_unref(Button), {
                                    size: "md",
                                    label: _ctx.$t('socialProfile.common.save'),
                                    disabled: !canSave.value || __props.loading,
                                    loading: __props.loading,
                                    onClick: handleSave
                                }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                            ]))
                            : (_openBlock(), _createBlock(_unref(Button), {
                                key: 1,
                                size: "md",
                                label: _ctx.$t('socialProfile.common.save'),
                                disabled: !canSave.value || __props.loading,
                                loading: __props.loading,
                                onClick: handleSave
                            }, null, 8 /* PROPS */, ["label", "disabled", "loading"]))
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", {
                            class: _normalizeClass(['flex flex-col', __props.platformPickerEnabled ? 'gap-4' : 'gap-x-4'])
                        }, [
                            (_unref(store).errorKey)
                                ? (_openBlock(), _createBlock(_unref(Alert), {
                                    key: 0,
                                    color: "danger",
                                    heading: _ctx.$t(_unref(store).errorKey)
                                }, null, 8 /* PROPS */, ["heading"]))
                                : _createCommentVNode("v-if", true),
                            (__props.error)
                                ? (_openBlock(), _createBlock(_unref(Alert), {
                                    key: 1,
                                    color: "danger",
                                    heading: __props.error
                                }, null, 8 /* PROPS */, ["heading"]))
                                : _createCommentVNode("v-if", true),
                            (__props.platformPickerEnabled && !isEdit.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 2,
                                    role: "radiogroup",
                                    "aria-label": _ctx.$t('socialProfile.common.platformPickerSubtitle'),
                                    class: "flex flex-wrap gap-5"
                                }, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableNetworks.value, (network) => {
                                        return (_openBlock(), _createBlock(_unref(CardSelectable), {
                                            key: network.id,
                                            icon: network.iconClass,
                                            title: network.name,
                                            selected: form.networkId === network.id,
                                            disabled: isNetworkDisabled(network.id),
                                            role: "radio",
                                            "aria-label": isNetworkDisabled(network.id)
                                                ? _ctx.$t('socialProfile.common.networkAlreadyAdded', { name: network.name })
                                                : network.name,
                                            class: "min-w-[184px] flex-1",
                                            onClick: ($event) => (selectNetwork(network.id))
                                        }, null, 8 /* PROPS */, ["icon", "title", "selected", "disabled", "aria-label", "onClick"]));
                                    }), 128 /* KEYED_FRAGMENT */))
                                ], 8 /* PROPS */, _hoisted_4))
                                : _createCommentVNode("v-if", true),
                            (!__props.platformPickerEnabled)
                                ? (_openBlock(), _createBlock(_unref(SelectInput), {
                                    key: 3,
                                    modelValue: form.networkId,
                                    "onUpdate:modelValue": [
                                        _cache[0] || (_cache[0] = ($event) => ((form.networkId) = $event)),
                                        onNetworkChange
                                    ],
                                    label: _ctx.$t('socialProfile.common.network'),
                                    options: networkOptions.value,
                                    required: true,
                                    disabled: loadingCatalog.value,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label", "options", "disabled"]))
                                : _createCommentVNode("v-if", true),
                            (__props.platformPickerEnabled)
                                ? (_openBlock(), _createBlock(_unref(UrlInput), {
                                    key: 4,
                                    modelValue: form.value,
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.value) = $event)),
                                    label: selectedNetwork.value?.name,
                                    prefix: selectedNetwork.value?.urlPrefix ?? '',
                                    "icon-left": selectedNetwork.value?.iconClass,
                                    placeholder: valuePlaceholder.value,
                                    error: valueError.value,
                                    disabled: !isEdit.value && !form.networkId,
                                    validator: acceptAnyValue,
                                    "full-width": "",
                                    onBlur: _cache[2] || (_cache[2] = ($event) => (valueTouched.value = true))
                                }, null, 8 /* PROPS */, ["modelValue", "label", "prefix", "icon-left", "placeholder", "error", "disabled"]))
                                : (_openBlock(), _createBlock(_unref(TextInput), {
                                    key: 5,
                                    modelValue: form.value,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((form.value) = $event)),
                                    label: _ctx.$t('socialProfile.common.value'),
                                    placeholder: valuePlaceholder.value,
                                    hint: valueHelp.value,
                                    required: true,
                                    error: valueError.value,
                                    maxlength: 255,
                                    "full-width": "",
                                    onBlur: _cache[4] || (_cache[4] = ($event) => (valueTouched.value = true))
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "hint", "error"])),
                            (!__props.platformPickerEnabled)
                                ? (_openBlock(), _createBlock(_unref(TextInput), {
                                    key: 6,
                                    "model-value": form.label ?? '',
                                    label: _ctx.$t('socialProfile.common.label'),
                                    placeholder: selectedNetwork.value?.name,
                                    maxlength: 100,
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => (form.label = $event || null))
                                }, null, 8 /* PROPS */, ["model-value", "label", "placeholder"]))
                                : _createCommentVNode("v-if", true)
                        ], 2 /* CLASS */)
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["model-value", "size", "footer-position"]),
                (__props.deleteEnabled)
                    ? (_openBlock(), _createBlock(_unref(ConfirmDeleteModal), {
                        key: 0,
                        modelValue: showDeleteConfirm.value,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((showDeleteConfirm).value = $event)),
                        title: _ctx.$t('socialProfile.common.deleteConfirmTitle'),
                        subtitle: _ctx.$t('socialProfile.common.deleteConfirmSubtitle'),
                        "confirm-label": _ctx.$t('socialProfile.common.deleteConfirmLabel'),
                        "cancel-label": _ctx.$t('form.cancel'),
                        onConfirm: handleDeleteConfirm,
                        onCancel: _cache[10] || (_cache[10] = ($event) => (showDeleteConfirm.value = false))
                    }, null, 8 /* PROPS */, ["modelValue", "title", "subtitle", "confirm-label", "cancel-label"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
